// frontend/admin-portal/src/pages/RfqDetail.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  PageHeader,
  Card,
  Button,
  Badge,
  Alert,
  Spinner,
  Table,
  Input,
  FileUploader,
  Modal,
} from "@fiberhome/shared-ui";
import { rfqService } from "../services/rfqService";
import RequisitionStatusBadge from "../components/procurement/RequisitionStatusBadge";

export default function RfqDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [rfq, setRfq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ---- Manual quotation form ----
  const [quoOpen, setQuoOpen] = useState(false);
  const [quoSaving, setQuoSaving] = useState(false);
  const [quoError, setQuoError] = useState("");
  const [quoMeta, setQuoMeta] = useState({
    currency: "IQD",
    validUntil: "",
    deliveryDays: "",
    paymentTerms: "",
    notes: "",
  });
  const [quoLines, setQuoLines] = useState([]);

  // ---- Import modal ----
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importError, setImportError] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [importMeta, setImportMeta] = useState({
    currency: "IQD",
    validUntil: "",
    deliveryDays: "",
    paymentTerms: "",
    notes: "",
  });

  // ---- Load ----
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await rfqService.get(id);
      setRfq(r);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Not found");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // ---- Export for supplier ----
  const handleExport = async () => {
    try {
      await rfqService.exportTemplate(rfq.id, rfq.code);
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  // ---- Open manual quotation form ----
  const openQuotationForm = () => {
    setQuoOpen(true);
    setQuoError("");
    setQuoMeta({
      currency: rfq.supplier?.currency || "IQD",
      validUntil: "",
      deliveryDays: "",
      paymentTerms: "",
      notes: "",
    });
    setQuoLines(
      (rfq.items || []).map((it) => ({
        rfqItemId: it.id,
        productName: it.productName,
        sku: it.productSku,
        quantity: it.quantity,
        unit: it.unit,
        available: true,
        unitPrice: "",
        leadTimeDays: "",
        brandOffered: "",
        notes: "",
      })),
    );
  };

  const updateLine = (idx, patch) => {
    const next = [...quoLines];
    next[idx] = { ...next[idx], ...patch };
    setQuoLines(next);
  };

  const submitQuotation = async () => {
    setQuoSaving(true);
    setQuoError("");
    try {
      await rfqService.submitQuotation(rfq.id, {
        ...quoMeta,
        validUntil: quoMeta.validUntil || null,
        deliveryDays:
          quoMeta.deliveryDays === "" ? null : Number(quoMeta.deliveryDays),
        items: quoLines.map((l) => ({
          rfqItemId: l.rfqItemId,
          available: !!l.available,
          unitPrice:
            l.available && l.unitPrice !== "" ? Number(l.unitPrice) : null,
          leadTimeDays: l.leadTimeDays === "" ? null : Number(l.leadTimeDays),
          brandOffered: l.brandOffered || null,
          notes: l.notes || null,
        })),
      });
      setQuoOpen(false);
      load();
    } catch (err) {
      setQuoError(
        err.response?.data?.message || err.message || "Submit failed",
      );
    } finally {
      setQuoSaving(false);
    }
  };

  // ---- Import handlers ----
  const openImport = () => {
    setImportOpen(true);
    setImportFile(null);
    setImportPreview(null);
    setImportResult(null);
    setImportError("");
    setImportMeta({
      currency: rfq.supplier?.currency || "IQD",
      validUntil: "",
      deliveryDays: "",
      paymentTerms: "",
      notes: "",
    });
  };

  const handleImportFile = async (files) => {
    const f = files[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) {
      setImportError("Please upload a .csv file");
      return;
    }
    setImportFile(f);
    setImportPreview(null);
    setImportResult(null);
    setImportError("");
    setImportBusy(true);
    try {
      const preview = await rfqService.importPreview(rfq.id, f);
      setImportPreview(preview);
    } catch (err) {
      setImportError(
        err.response?.data?.message || err.message || "Preview failed",
      );
      setImportFile(null);
    } finally {
      setImportBusy(false);
    }
  };

  const confirmImport = async () => {
    if (!importFile) return;
    setImportBusy(true);
    setImportError("");
    try {
      const result = await rfqService.importCommit(
        rfq.id,
        importFile,
        importMeta,
      );
      setImportResult(result);
      load();
    } catch (err) {
      setImportError(
        err.response?.data?.message || err.message || "Import failed",
      );
    } finally {
      setImportBusy(false);
    }
  };

  // ---- Render states ----
  if (loading) {
    return (
      <>
        <PageHeader title="RFQ" breadcrumbs={["Home", "Procurement", "…"]} />
        <Card>
          <div className="fh-settings-loading">
            <Spinner size="lg" />
          </div>
        </Card>
      </>
    );
  }

  if (error || !rfq) {
    return (
      <>
        <PageHeader title="Not found" />
        <Alert variant="danger">{error || "RFQ not found"}</Alert>
        <Button
          variant="outline"
          className="mt-3"
          onClick={() => navigate("/requisitions")}
        >
          ← Back to Requisitions
        </Button>
      </>
    );
  }

  const quotation = rfq.quotation;
  const quoItemsByRfqItem = new Map(
    (quotation?.items || []).map((qi) => [qi.rfqItemId, qi]),
  );

  return (
    <>
      <PageHeader
        title={`${rfq.code} — ${rfq.supplier?.name || ""}`}
        subtitle={`Requisition: ${rfq.requisition?.code} · ${rfq.requisition?.title || ""}`}
        breadcrumbs={["Home", "Procurement", "RFQs", rfq.code]}
        actions={
          <div className="d-flex gap-2 flex-wrap">
            <Button variant="ghost" onClick={handleExport}>
              Export for Supplier
            </Button>

            {!quotation && rfq.status !== "cancelled" && (
              <>
                <Button variant="outline" onClick={openImport}>
                  Import Supplier Response
                </Button>
                <Button variant="primary" onClick={openQuotationForm}>
                  Record Manually
                </Button>
              </>
            )}

            <Button
              variant="ghost"
              onClick={() => navigate(`/requisitions/${rfq.requisitionId}`)}
            >
              Back to Requisition
            </Button>
          </div>
        }
      />

      {/* ---------- Status + Supplier Cards ---------- */}
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Card title="Status">
            <div className="fh-kv">
              <div className="fh-kv__row">
                <span className="text-muted">RFQ Status</span>
                <RequisitionStatusBadge status={rfq.status} />
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Sent</span>
                <span>
                  {rfq.sentAt ? new Date(rfq.sentAt).toLocaleString() : "—"}
                </span>
              </div>
              <div className="fh-kv__row">
                <span className="text-muted">Due</span>
                <span>
                  {rfq.dueAt ? new Date(rfq.dueAt).toLocaleDateString() : "—"}
                </span>
              </div>
              {quotation && (
                <div className="fh-kv__row">
                  <span className="text-muted">Quotation</span>
                  <Badge variant="primary">{quotation.code}</Badge>
                </div>
              )}
            </div>
          </Card>
        </div>

        <div className="col-md-8">
          <Card title="Supplier">
            <div className="row g-3">
              <div className="col-md-6">
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Name
                </div>
                <div style={{ fontWeight: 600 }}>{rfq.supplier?.name}</div>
              </div>
              <div className="col-md-6">
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Contact
                </div>
                <div>{rfq.supplier?.contactName || "—"}</div>
              </div>
              <div className="col-md-6">
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Email
                </div>
                <div>{rfq.supplier?.email || "—"}</div>
              </div>
              <div className="col-md-6">
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Phone
                </div>
                <div>{rfq.supplier?.phone || "—"}</div>
              </div>
              {rfq.message && (
                <div className="col-12">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Message
                  </div>
                  <div style={{ whiteSpace: "pre-wrap" }}>{rfq.message}</div>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* ---------- Manual Quotation Form ---------- */}
      {quoOpen && !quotation && (
        <Card title="Enter Supplier Quotation" className="mb-3">
          {quoError && (
            <Alert variant="danger" className="mb-3">
              {quoError}
            </Alert>
          )}

          <div className="row g-3 mb-3">
            <div className="col-md-3">
              <Input
                label="Currency"
                value={quoMeta.currency}
                onChange={(e) =>
                  setQuoMeta({ ...quoMeta, currency: e.target.value })
                }
              />
            </div>
            <div className="col-md-3">
              <Input
                label="Valid Until"
                type="date"
                value={quoMeta.validUntil}
                onChange={(e) =>
                  setQuoMeta({ ...quoMeta, validUntil: e.target.value })
                }
              />
            </div>
            <div className="col-md-3">
              <Input
                label="Delivery Days"
                type="number"
                value={quoMeta.deliveryDays}
                onChange={(e) =>
                  setQuoMeta({ ...quoMeta, deliveryDays: e.target.value })
                }
              />
            </div>
            <div className="col-md-3">
              <Input
                label="Payment Terms"
                value={quoMeta.paymentTerms}
                onChange={(e) =>
                  setQuoMeta({ ...quoMeta, paymentTerms: e.target.value })
                }
              />
            </div>
            <div className="col-12">
              <Input
                label="Notes"
                value={quoMeta.notes}
                onChange={(e) =>
                  setQuoMeta({ ...quoMeta, notes: e.target.value })
                }
              />
            </div>
          </div>

          <div className="fh-quo-lines">
            <div className="fh-quo-lines__head">
              <div>Item</div>
              <div>Qty</div>
              <div className="text-center">Available?</div>
              <div>Unit Price</div>
              <div>Lead Time</div>
              <div>Brand</div>
              <div>Notes</div>
            </div>
            {quoLines.map((l, idx) => (
              <div className="fh-quo-lines__row" key={l.rfqItemId}>
                <div>
                  <div style={{ fontWeight: 600 }}>{l.productName}</div>
                  <div className="text-muted" style={{ fontSize: 11.5 }}>
                    {l.sku}
                  </div>
                </div>
                <div className="text-muted">
                  {l.quantity} {l.unit}
                </div>
                <div className="text-center">
                  <input
                    type="checkbox"
                    checked={l.available}
                    onChange={(e) =>
                      updateLine(idx, { available: e.target.checked })
                    }
                  />
                </div>
                <div>
                  <input
                    className="fh-input"
                    type="number"
                    step="0.01"
                    disabled={!l.available}
                    value={l.unitPrice}
                    onChange={(e) =>
                      updateLine(idx, { unitPrice: e.target.value })
                    }
                    placeholder={l.available ? "0.00" : "n/a"}
                  />
                </div>
                <div>
                  <input
                    className="fh-input"
                    type="number"
                    disabled={!l.available}
                    value={l.leadTimeDays}
                    onChange={(e) =>
                      updateLine(idx, { leadTimeDays: e.target.value })
                    }
                    placeholder={l.available ? "days" : "—"}
                  />
                </div>
                <div>
                  <input
                    className="fh-input"
                    disabled={!l.available}
                    value={l.brandOffered}
                    onChange={(e) =>
                      updateLine(idx, { brandOffered: e.target.value })
                    }
                  />
                </div>
                <div>
                  <input
                    className="fh-input"
                    value={l.notes}
                    onChange={(e) => updateLine(idx, { notes: e.target.value })}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button
              variant="ghost"
              onClick={() => setQuoOpen(false)}
              disabled={quoSaving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={submitQuotation}
              loading={quoSaving}
            >
              Save Quotation
            </Button>
          </div>
        </Card>
      )}

      {/* ---------- Quotation Summary ---------- */}
      {quotation && (
        <Card title={`Quotation ${quotation.code}`} className="mb-3">
          <div className="row g-3">
            <div className="col-md-3">
              <div className="text-muted" style={{ fontSize: 12 }}>
                Currency
              </div>
              <Badge variant="info">{quotation.currency}</Badge>
            </div>
            <div className="col-md-3">
              <div className="text-muted" style={{ fontSize: 12 }}>
                Total
              </div>
              <div style={{ fontWeight: 700, fontSize: 18 }}>
                {quotation.currency}{" "}
                {Number(quotation.totalAmount).toLocaleString()}
              </div>
            </div>
            <div className="col-md-3">
              <div className="text-muted" style={{ fontSize: 12 }}>
                Delivery
              </div>
              <div>{quotation.deliveryDays ?? "—"} day(s)</div>
            </div>
            <div className="col-md-3">
              <div className="text-muted" style={{ fontSize: 12 }}>
                Valid Until
              </div>
              <div>{quotation.validUntil || "—"}</div>
            </div>
          </div>
        </Card>
      )}

      {/* ---------- RFQ Items ---------- */}
      <Card title="RFQ Items">
        <Table
          columns={[
            {
              key: "product",
              label: "Item",
              render: (it) => (
                <div>
                  <div style={{ fontWeight: 600 }}>{it.productName}</div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {it.productSku}
                  </div>
                </div>
              ),
            },
            {
              key: "quantity",
              label: "Qty",
              render: (it) => `${it.quantity} ${it.unit}`,
            },
            {
              key: "status",
              label: "Supplier Response",
              render: (it) => {
                const qi = quoItemsByRfqItem.get(it.id);
                if (!qi) return <Badge variant="default">awaiting</Badge>;
                if (!qi.available)
                  return <Badge variant="danger">not available</Badge>;
                return <Badge variant="success">quoted</Badge>;
              },
            },
            {
              key: "unitPrice",
              label: "Unit Price",
              render: (it) => {
                const qi = quoItemsByRfqItem.get(it.id);
                if (!qi || !qi.available)
                  return <span className="text-muted">—</span>;
                return Number(qi.unitPrice).toLocaleString();
              },
            },
            {
              key: "lineTotal",
              label: "Line Total",
              render: (it) => {
                const qi = quoItemsByRfqItem.get(it.id);
                if (!qi || !qi.available)
                  return <span className="text-muted">—</span>;
                return <strong>{Number(qi.lineTotal).toLocaleString()}</strong>;
              },
            },
            {
              key: "leadTime",
              label: "Lead Time",
              render: (it) => {
                const qi = quoItemsByRfqItem.get(it.id);
                if (!qi || !qi.available)
                  return <span className="text-muted">—</span>;
                return qi.leadTimeDays != null ? `${qi.leadTimeDays} d` : "—";
              },
            },
          ]}
          data={rfq.items || []}
          emptyMessage="No items"
        />
      </Card>

      {/* ---------- Import Modal ---------- */}
      <Modal
        open={importOpen}
        onClose={importBusy ? undefined : () => setImportOpen(false)}
        title={importResult ? "Import Complete" : "Import Supplier Response"}
        size="lg"
        footer={
          importResult ? (
            <Button
              variant="primary"
              onClick={() => {
                setImportOpen(false);
                load();
              }}
            >
              Done
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={() => setImportOpen(false)}
                disabled={importBusy}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={confirmImport}
                loading={importBusy}
                disabled={!importPreview || importPreview.summary.valid === 0}
              >
                Import {importPreview?.summary?.valid || 0} row(s)
              </Button>
            </>
          )
        }
      >
        {importError && (
          <Alert variant="danger" className="mb-3">
            {importError}
          </Alert>
        )}

        {!importPreview && !importResult && (
          <>
            <Alert variant="info" className="mb-3">
              Upload the CSV returned by the supplier. Get the template with
              <strong> Export for Supplier</strong> first — the supplier fills
              in
              <code> available</code>, <code>unit_price</code>, and other
              columns, then sends the file back.
            </Alert>
            <FileUploader
              accept=".csv,text/csv"
              multiple={false}
              maxSizeMB={5}
              onFiles={handleImportFile}
              label="Choose CSV file"
              dragLabel="Drop the supplier's CSV here or click to browse"
            />
            {importBusy && (
              <div className="d-flex align-items-center gap-2 mt-3">
                <Spinner size="sm" />
                <span className="text-muted">Parsing…</span>
              </div>
            )}
          </>
        )}

        {importPreview && !importResult && (
          <>
            <Alert variant="success" className="mb-3">
              Parsed <strong>{importPreview.summary.total}</strong> rows —{" "}
              <strong>{importPreview.summary.valid}</strong> valid,{" "}
              <strong>{importPreview.summary.errors}</strong> with errors.
            </Alert>

            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <Input
                  label="Currency"
                  value={importMeta.currency}
                  onChange={(e) =>
                    setImportMeta({ ...importMeta, currency: e.target.value })
                  }
                />
              </div>
              <div className="col-md-4">
                <Input
                  label="Valid Until"
                  type="date"
                  value={importMeta.validUntil}
                  onChange={(e) =>
                    setImportMeta({ ...importMeta, validUntil: e.target.value })
                  }
                />
              </div>
              <div className="col-md-4">
                <Input
                  label="Delivery Days"
                  type="number"
                  value={importMeta.deliveryDays}
                  onChange={(e) =>
                    setImportMeta({
                      ...importMeta,
                      deliveryDays: e.target.value,
                    })
                  }
                />
              </div>
              <div className="col-12">
                <Input
                  label="Payment Terms"
                  value={importMeta.paymentTerms}
                  onChange={(e) =>
                    setImportMeta({
                      ...importMeta,
                      paymentTerms: e.target.value,
                    })
                  }
                />
              </div>
              <div className="col-12">
                <Input
                  label="Notes"
                  value={importMeta.notes}
                  onChange={(e) =>
                    setImportMeta({ ...importMeta, notes: e.target.value })
                  }
                />
              </div>
            </div>

            <Table
              columns={[
                { key: "line", label: "#", width: 50 },
                {
                  key: "name",
                  label: "Item",
                  render: (r) => (
                    <div>
                      <div style={{ fontWeight: 600 }}>{r.name}</div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        {r.sku}
                      </div>
                    </div>
                  ),
                },
                { key: "quantity", label: "Qty" },
                {
                  key: "available",
                  label: "Avail",
                  render: (r) => (
                    <Badge variant={r.available ? "success" : "danger"}>
                      {r.available ? "yes" : "no"}
                    </Badge>
                  ),
                },
                {
                  key: "unitPrice",
                  label: "Unit Price",
                  render: (r) => r.unitPrice ?? "—",
                },
                {
                  key: "leadTimeDays",
                  label: "Lead",
                  render: (r) => r.leadTimeDays ?? "—",
                },
                {
                  key: "errors",
                  label: "Issues",
                  render: (r) =>
                    r.errors.length === 0 ? (
                      <span className="text-muted">—</span>
                    ) : (
                      <ul className="fh-import-issues">
                        {r.errors.map((e, i) => (
                          <li
                            key={i}
                            className="fh-import-issue fh-import-issue--error"
                          >
                            {e}
                          </li>
                        ))}
                      </ul>
                    ),
                },
              ]}
              data={importPreview.rows}
              emptyMessage="No rows"
            />
          </>
        )}

        {importResult && (
          <>
            <Alert variant="success" className="mb-3">
              Imported <strong>{importResult.summary.imported}</strong> lines —
              total{" "}
              <strong>
                {Number(importResult.summary.total).toLocaleString()}
              </strong>
            </Alert>
            <div style={{ fontSize: 13, color: "var(--fh-text-muted)" }}>
              The supplier's response has been saved as a quotation on this RFQ.
              You can now view it above or return to the requisition to compare.
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
